import React, {useEffect} from 'react';
import {Link} from 'react-router';
import InfoBox from "../../components/passengerInformation/InfoBox";
import ThemableSvgIcon from "../../components/theme/ThemableSvgIcon";
import BackLink from "../../components/BackLink";
import {usePreCruiseContext} from "../../context/PreCruiseContext";
import {getFromHub} from "../../actions/networkActions";
import { useLingui } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { getBackToDashboardText, getCCPageInfoText } from '../../utils/themeUtils';

function PassengersOverview(props) {
    const {preCruiseInfo, setPreCruiseInfo} = usePreCruiseContext();
    const {t} = useLingui();
    useEffect(() => {
        let isCancelled = false;

        const fetchData = async () => {
            const response = await getFromHub('precruise/status');
            if(!isCancelled) {
                setPreCruiseInfo(response);
            }
        }

        fetchData();

        return () => {
            isCancelled = true;
        }
    }, []);


    const {passengers = []} = preCruiseInfo;

    const {type} = props.params;
    const isCCPage = type === "cc";
    const isMedicalMobilityPage = type === "medicalAndMobility";

    const ccPageInfoText = getCCPageInfoText();

    const getHeaderText = () => {
        if(isCCPage){
           return t({ id: "card_registration_dashboard"});
        }else if(isMedicalMobilityPage){
            return t({ id: "medical_n_mobility_information_dashboard"});
        }
        return t({ id: "travel_information_dashboard"});
    }

    return (
        <div className="container mx-auto">
            <div className="mt-4">
                <BackLink to={t({id: "/dashboard"})} text={getBackToDashboardText()} />
            </div>
            <h1>{getHeaderText()}</h1>

            {(isCCPage && ccPageInfoText) ? (
                <div className="bg-grey-lighter pt-4">
                    <div className="container mx-auto">
                        <div className="flex">
                            <ThemableSvgIcon name="info-inverted" className="shrink-0 w-6 h-6 text-primary-dark"/>
                            <div className="ml-2 text-left leading-normal">
                                <p>{ccPageInfoText}</p>
                            </div>
                        </div>
                    </div>
                </div>
            ) : null}

            <div className="bg-primary-lightest-25 py-8 my-8 shadow">
                <div className="container mx-auto">
                    <div className="grid gap-x-4 gap-y-6 sm:grid-cols-2 xl:grid-cols-3">
                        {passengers.map(passenger => (
                            <PassengerTile 
                                key={passenger.passengerId} 
                                passenger={passenger} 
                                isCCpage={isCCPage} 
                                isMedicalMobilityPage={isMedicalMobilityPage}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default PassengersOverview;

function PassengerTile(props) {

    const {t} = useLingui();

    const passenger = props.passenger;
    const passengerName = `${passenger.firstName} ${passenger.lastName}`;
    const isAdult = passenger.type === "Adult";

    const getStatus = () => {
        if (props.isCCpage) {
            return passenger.cardRegistrationStatus;
        }
        if (props.isMedicalMobilityPage) {
            return passenger.prerequisiteQuestionnaireStatus;
        }
        return passenger.travelInfoStatus;
    };
    
    const isComplete = getStatus() === "COMPLETE";

    let shouldUnlock = false;
    if(props.isMedicalMobilityPage){
        shouldUnlock = passenger.isTravelInfoSecured; 
    }else if(props.isCCpage) {
        shouldUnlock = passenger.isTravelInfoSecured && (passenger.cardRegistrationStatus === "COMPLETE" || passenger.cardOptedOut === true);
    }else{
        shouldUnlock = passenger.shouldAuthenticate && passenger.travelInfoStatus === "COMPLETE";
    }

    const infoIcon = shouldUnlock ? "lock" : passenger.isLead ? "adult" : "";

    let infoText = "";
    
    if (shouldUnlock) {
        infoText = t({id: "information_locked"});
    } else if (passenger.isLead) {
        infoText = t({id: "lead_guest"});
    }

    const getLinkDetailsByPage = (passenger, shouldUnlock) => {
        if (props.isCCpage) {
            return getLinkDetailsCC(passenger, shouldUnlock);
        }
        if (props.isMedicalMobilityPage) {
            return getLinkDetailsMedicalMobility(passenger, shouldUnlock);
        }
        return getLinkDetails(passenger, shouldUnlock);
    };

    const { to, text } = getLinkDetailsByPage(passenger, shouldUnlock);

    return (
        <div className="flex flex-col bg-white border text-center p-4 shadow">
            <div>
                <ThemableSvgIcon name={passenger.type.toLowerCase()} className="w-18 h-18 inline-block text-primary-dark"/>
            </div>

            <div className="mt-4 text-lg font-bold text-primary-dark">
                {passengerName}
            </div>

            <div className="mt-2">
                {isComplete &&
                    <div className="flex items-center justify-center text-left">
                        <ThemableSvgIcon name="tick-circle" className="w-6 h-6 inline-block text-success mr-2 shrink-0"/>
                        <span>{props.isCCpage ? t({id: "registration_complete"}) : t({id: "information_submitted"}) }</span>
                    </div>
                }

                    <Link className="inline-block text-primary underline mt-2"
                          to={to}>
                        {text} <ThemableSvgIcon name="chevron-right" className="w-4 h-4 inline"/>
                    </Link>
            </div>


            {infoText &&
            <div className="grow flex items-end">
                <InfoBox infoText={infoText} icon={infoIcon} classes="w-full mt-4"/>
            </div>
            }
        </div>
    );
}

function getLinkDetails(passenger, shouldUnlock) {    
    // travel info not yet completed
    if(passenger.travelInfoStatus !== "COMPLETE") {
        return {
            to: t({id: "/precruise/passenger"}) + `/${passenger.passengerId}`,
            text: t({id: "information_required"}),
        }
    }

    // passenger has completed the survey
    return {
        to: shouldUnlock ?
            t({id: "/precruise/passenger"}) + `/${passenger.passengerId}` + t({id: "/unlock"}) :
            t({id: "/precruise/passenger"}) + `/${passenger.passengerId}` + t({id: "/review"}),
        text: t({id: "view_and_edit"}),
    }
}

function getLinkDetailsCC(passenger, shouldUnlock) {
    // if passenger has locked their travel info
    // user visiting for the first time will be allowed without otp
    // User needs to Enter OTP before editing since visiting second time
    if(shouldUnlock) {
        return {
            to: t({id: "/precruise/passenger"}) + `/${passenger.passengerId}` + t({id: "/unlock"}) + '/cc',
            text: t({id: "unlock_to_add_edit"}),
        }
    }

    // passenger has not yet completed the card registration
    if(passenger.cardRegistrationStatus !== "COMPLETE") {
        return {
            to: t({id: "/precruise/passenger"}) + `/${passenger.passengerId}/cc`,
            text: t({id: "register_card"}),
        }
    }

    // passenger has already completed the card registration
    return {
        to: t({id: "/precruise/passenger"}) + `/${passenger.passengerId}/cc`,
        text: t({id: "edit"}),
    }

}

function getLinkDetailsMedicalMobility(passenger, shouldUnlock){
    // Travel Information Locked and not yet completed the survey
    if(shouldUnlock && passenger.prerequisiteQuestionnaireStatus !== "COMPLETE") {
        return {
            to: t({id: "/precruise/passenger"}) + `/${passenger.passengerId}` + t({id: "/unlock"}) + '/medicalAndMobility',
            text: t({id: "unlock_to_add_edit"}),
        }
    }

    // Not yet completed the survey
    if(passenger.prerequisiteQuestionnaireStatus !== "COMPLETE") {
        return {
            to: t({id: "/precruise/passenger"}) + `/${passenger.passengerId}/medicalAndMobility`,
            text: t({id: "information_required"}),
        }
    }

    // Completed the survey
    // To be reviewed after lock
    return {
        to: shouldUnlock ?
            t({id: "/precruise/passenger"}) + `/${passenger.passengerId}` + t({id: "/unlock"}) + '/medicalAndMobility' :
            t({id: "/precruise/passenger"}) + `/${passenger.passengerId}` + t({id: "/review"}) + '/medicalAndMobility',
        text: t({id: "view_and_edit"}),
    }
}